import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray, change } from 'redux-form';
import { t } from 'i18next';
import RenderField from '../../ReactComponent/RenderField';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import Plus from '../../../assets/icons/Plus.svg';
import Delete from '../../../assets/icons/RemoveOrange.svg';
import Edit from '../../../assets/icons/Edit.svg';
import PasswordGroup from '../../ReactComponent/PasswordGroup';
import {
  isRequired, isEmail, maxChar10, maxChar40,
} from '../../../utils/validator';
import { digitOnly } from '../../../utils/mask';

import {
  AddItemContainer,
  ContentBoxLightGrey,
  CategoryTitle,
  CardTitle,
  FileUploadLabel,
  FileUploadContainer,
  FileUpload,
  ItemContainer,
  Icon,
  EditButtonGroup,
  LineButton,
  TextProperty,
  FullRow,
  CompanyFormContainer,
  Label,
  CheckboxContainer,
  DivisionRadioContainer,
} from '../css';
import { P, H3 } from '../../../theme/typography';
import { BillingAccountType } from '../../../utils/enums';

import Tooltip from '../../ReactComponent/Tooltip';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import RadioButton from '../../../lib/ui/form_elements/RadioButton';
import PrimaryButton from '../../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../../lib/ui/buttons/SecondaryButton';

const permissionRenderer = ({ item }) => (
  <H3>{t(item.name ? item.name : item)}</H3>
);

const renderAccountList = ({
  fields, dispatch, formName, currentAccountList, error,
}) => {
  const disableAddItem = !!error.accountList;
  const addItem = (index) => {
    dispatch(change(formName, `accountList[${index}].completed`, true));
  };

  const removeItem = (index) => {
    const newAccountlist = [...currentAccountList];
    newAccountlist.splice(index, 1);
    if (newAccountlist.length === 0) {
      newAccountlist.push({});
    }

    dispatch(change(formName, 'accountList', newAccountlist));
  };

  const [state, setState] = useState({
    showAddLineButton: false,
    hideRemoveButton: true,
  });

  useEffect(() => {
    const hideRemoveButton = currentAccountList.length <= 1;
    const showAddLineButton = !(currentAccountList.some((a) => !a.completed));
    setState({
      ...state,
      hideRemoveButton,
      showAddLineButton,
    });
  }, [currentAccountList]);
  const editAccount = (index, dispatch) => dispatch(change(formName, `accountList[${index}].completed`, false));

  const deleteAccount = (currentAccountList, index, dispatch) => {
    const newlist = [...currentAccountList];
    newlist.splice(index, 1);
    if (newlist.length === 0) {
      newlist.push({});
    }

    dispatch(change(formName, 'accountList', newlist));
  };

  return (
    <>
      {fields.map((item, index) => {
        const fieldValue = fields.get(index);
        const {
          completed,
          accountNumber,
          accountType,
        } = fieldValue;
        if (completed) {
          return (
            <ContentBoxLightGrey key={index}>
              <Row>
                <Column $tablet={6} $mobile={12}>
                  <CardTitle>
                    {t('Account')}
                    {` ${index + 1}`}
                  </CardTitle>
                </Column>
                <Column $tablet={6} $mobile={12}>
                  <EditButtonGroup>
                    <LineButton onClick={() => deleteAccount(currentAccountList, index, dispatch)}>
                      <Icon className="icon" src={Delete} alt="delete" />
                      {t('Delete')}
                    </LineButton>
                    {state.showAddLineButton && (
                    <LineButton onClick={() => editAccount(index, dispatch)}>
                      <Icon className="icon" src={Edit} alt="edit" />
                      {t('Edit')}
                    </LineButton>
                    )}
                  </EditButtonGroup>
                </Column>
              </Row>

              <Row>
                <Column>
                  <TextProperty>
                    <P className="title">{t('BillingAccountNumber')}</P>
                    <P className="text">{accountNumber}</P>
                  </TextProperty>
                </Column>
                <Column>
                  <TextProperty>
                    <P className="title">{t('AccountType')}</P>
                    <P className="text">{t(accountType)}</P>
                  </TextProperty>
                </Column>
              </Row>
            </ContentBoxLightGrey>
          );
        }

        return (
          <ContentBoxLightGrey className="edit-item-box" key={index}>
            <Row>
              <Column $tablet={6} $mobile={12}>
                <Field
                  name={`${item}.accountNumber`}
                  type="text"
                  required
                  component={TextInput}
                  label={t('AccountNumber')}
                  normalize={digitOnly}
                  validate={[isRequired, maxChar10]}
                />
              </Column>
              <Column $tablet={6} $mobile={12}>
                <Label>
                  {t('AccountType')}
                  <Tooltip tip={t('TipAccountType')} />
                </Label>
                <CheckboxContainer>
                  <Field
                    name={`${item}.accountType`}
                    type="checkboxes"
                    required
                    data={[BillingAccountType.Billing, BillingAccountType.Protected]}
                    component={RenderField}
                  />
                </CheckboxContainer>
              </Column>

            </Row>
            <AddItemContainer>
              {state.hideRemoveButton ? undefined
                : (
                  <SecondaryButton onClick={() => removeItem(index)} showArrow={false}>
                    {t('RemoveItem')}
                  </SecondaryButton>
                )}
              <PrimaryButton
                disabled={disableAddItem}
                onClick={() => addItem(index)}
                showArrow={false}
              >
                {t('SaveItem')}
              </PrimaryButton>
            </AddItemContainer>
          </ContentBoxLightGrey>
        );
      })}
      {state.showAddLineButton && (
        <FileUploadContainer className="with-dot">
          <FileUpload onClick={() => {
            fields.push({});
          }}
          >
            <img src={Plus} alt="add more part file" />
            <FileUploadLabel>{t('AddAnotherItem')}</FileUploadLabel>
          </FileUpload>
        </FileUploadContainer>
      )}
    </>
  );
};

const CompanyForm = ({
  formData = {}, formName, dispatch, permissionList, deleteCompany, parentFields, error,
}) => {
  const {
    accountList = [], rowVersion, companyName, email,
  } = formData;
  const {
    passwordImage,
  } = parentFields;
  const update = !!rowVersion;
  return (
    <CompanyFormContainer>

      <Row className="company-fields">
        <Column $tablet={12} $mobile={12}>
          <CategoryTitle>{t('Division')}</CategoryTitle>
          <DivisionRadioContainer>
            <Field
              name="division"
              component={RadioButton}
              options={[
                { label: t('DivisionTruckmate'), value: 'Truckmate', defaultChecked: true },
                { label: t('DivisionSameday'), value: 'Sameday' },
                { label: t('DivisionFreight'), value: 'Freight' },
              ]}
              disabled={update}
              validate={[isRequired]}
            />
          </DivisionRadioContainer>
        </Column>
      </Row>

      <Row className="company-fields">
        <Column $tablet={6} $mobile={12}>
          {update
            ? (
              <TextProperty>
                <P className="title">{t('CompanyName')}</P>
                <P className="text">{companyName}</P>
              </TextProperty>
            )
            : (
              <Field
                name="companyName"
                type="text"
                required
                component={TextInput}
                label={t('CompanyName')}
                validate={[isRequired, maxChar40]}
                maxLength={40}
              />
            )}
        </Column>
        <Column $tablet={6} $mobile={12}>
          {update
            ? (
              <TextProperty>
                <P className="title">{t('Email')}</P>
                <P className="text">{email}</P>
              </TextProperty>
            )
            : (
              <Field
                name="email"
                type="text"
                required
                component={TextInput}
                label={t('Email')}
                validate={[isRequired, isEmail]}
              />
            )}
        </Column>
        <Column $tablet={12} $mobile={12}>
          <Field
            name="description"
            type="text"
            required
            component={TextInput}
            label={t('Description')}
            validate={[isRequired, maxChar40]}
            maxLength={40}
          />
        </Column>
      </Row>
      {!update && (
      <>
        <Row className="company-fields">
          <Column $tablet={10} $mobile={12}>
            <PasswordGroup passwordImage={passwordImage} />
          </Column>
        </Row>
        <TextProperty>
          {parentFields.checkboxText && parentFields.checkboxText.value}
        </TextProperty>
        <Row className="company-fields">
          <Column>
            <Field
              name="changePassword"
              type="checkboxes"
              data={['Force User To Reset Password']}
              component={RenderField}
              multiple
            />
          </Column>
        </Row>
      </>
      )}
      <ItemContainer>
        <FieldArray
          name="accountList"
          dispatch={dispatch}
          component={renderAccountList}
          formName={formName}
          currentAccountList={accountList}
          error={error}
        />
      </ItemContainer>
      { permissionList && permissionList.length > 0
        && (
        <>
          <CategoryTitle>
            {parentFields.categoryTitle2 && parentFields.categoryTitle2.value}
          </CategoryTitle>
          <Row className="section-margin">
            {permissionList.map((permission, index) => (
              permission.permissionName !== 'RequestPickup'
              && permission.permissionName !== 'PrintLabels'
              && (
              <Column $tablet={4} $mobile={12} key={index}>
                <FullRow>
                  <Field
                    name={`permissions.${permission.permissionName}.selected`}
                    type="checkboxes"
                    data={[{ name: permission.permissionName, desc: permission.description, code: permission.permissionId }]}
                    component={RenderField}
                    renderer={permissionRenderer}
                    multiple
                    value={permission.code}
                    disabled={permission.permissionName === 'Shipping'}
                  />
                </FullRow>
              </Column>
              )
            ),
            )}
          </Row>
        </>
        )}
      <CategoryTitle>
        {t('DisableCompany')}
      </CategoryTitle>
      <TextProperty>
        {parentFields.disableCompanyCopy && parentFields.disableCompanyCopy.value}
      </TextProperty>
      <Row>
        <Column className="section-margin">
          <Field
            name="disabled"
            type="checkboxes"
            data={[t('DisableCompany')]}
            component={RenderField}
            multiple
          />
        </Column>
      </Row>
      <CategoryTitle>
        {t('DeleteCompany')}
      </CategoryTitle>
      <TextProperty>
        {parentFields.deleteCompanyCopy && parentFields.deleteCompanyCopy.value}
      </TextProperty>
      <PrimaryButton type="button" className={`${update && 'active'}`} disabled={!update} onClick={() => deleteCompany()}>
        {t('DeleteCompany')}
      </PrimaryButton>
    </CompanyFormContainer>
  );
};

CompanyForm.propTypes = {
  formData: PropTypes.shape({
    accountList: PropTypes.array,
    rowVersion: PropTypes.string,
    companyName: PropTypes.string,
    email: PropTypes.string,
    description: PropTypes.string,
  }),
  formName: PropTypes.string,
  dispatch: PropTypes.func,
  permissionList: PropTypes.array,
  deleteCompany: PropTypes.func,
  parentFields: PropTypes.shape({
    passwordImage: PropTypes.string,
    checkboxText: PropTypes.object,
    categoryTitle2: PropTypes.object,
    disableCompanyCopy: PropTypes.object,
    deleteCompanyCopy: PropTypes.object,
  }),
  error: PropTypes.object,
};

export default CompanyForm;
